<template>
  <div class="view crm customer">
    <div class="page" v-if="customer">
      <a-page-header
        class="rounded bg-gray-1"
        :title="'Customer Details'"
        :sub-title="`${customer.name ? customer.name : ''} ${customer.last_name ? customer.last_name : ''}`"
        @back="() => $router.push('/crm/customers')"
      >
        <template v-slot:tags>
          <a-tag color="blue">
            {{ String(customer.status).toUpperCase() }}
          </a-tag>
        </template>

        <template slot="extra">
          <a-button key="2" @click="onNewProject"> {{ $t('crm.details.add-project') }} </a-button>
          <a-button key="1" type="primary" @click="onSave"> {{ $t('crm.details.save') }} </a-button>
        </template>

        <a-descriptions :column="2">
          <a-descriptions-item label="Customer ID" span="2">
            <input class="idbox" :value="customer._id" />
          </a-descriptions-item>
          <a-descriptions-item label="Creator" v-if="customer.created_by">
            {{ customer.created_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Updator" v-if="customer.updated_by">
            {{ customer.updated_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Created At" v-if="customer.created_at">
            <span class="badge badge-primary"> {{ customer.created_at | moment('lll') }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="Updated At" v-if="customer.updated_at">
            <span class="badge badge-success"> {{ customer.updated_at | moment('lll') }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-page-header>

      <div class="content">
        <a-tabs v-model="current_tab" :defaultActiveKey="1" class="kit-tabs kit-tabs-bold">
          <a-tab-pane :key="1" tab="Basic Info" class="px-2 py-4">
            <a-form-model :model="customer" :label-col="labelCol" :wrapper-col="wrapperCol" compact>
              <h5 class="text-black mt-2 mb-3"><strong>Customer Personal Information</strong></h5>

              <!-- Name -->
              <a-form-model-item label="Name/Last Name">
                <a-input-group compact>
                  <a-input v-model="customer.name" style="width: 50%" placeholder="Name" />
                  <a-input v-model="customer.last_name" style="width: 50%" placeholder="Last Name" />
                </a-input-group>
              </a-form-model-item>

              <!-- Birthday -->
              <a-form-model-item label="Birthday">
                <a-date-picker v-model="customer.birthday" />
              </a-form-model-item>

              <!-- Gender -->
              <a-form-model-item label="Gender">
                <a-radio-group v-model="customer.gender" :defaultValue="'unknown'">
                  <a-radio-button value="unknown"> Unknown </a-radio-button>
                  <a-radio-button value="male"> Male </a-radio-button>
                  <a-radio-button value="female"> Female </a-radio-button>
                </a-radio-group>
              </a-form-model-item>

              <!-- Organization -->
              <a-form-model-item label="Organization">
                <a-input v-model="customer.organization" placeholder="Organization" />
              </a-form-model-item>

              <!-- Email -->
              <a-form-model-item label="Email">
                <a-input v-model="customer.email" placeholder="Email" />
              </a-form-model-item>

              <!-- Phone -->
              <a-form-model-item label="Phone">
                <a-input-group compact>
                  <a-input v-model="customer.phone" style="width: 50%" placeholder="Number" />
                  <a-input v-model="customer.phone_extension" style="width: 50%" placeholder="Extension" />
                </a-input-group>
              </a-form-model-item>

              <!-- Secondary Phone -->
              <a-form-model-item label="Secondary Phone">
                <a-input-group compact>
                  <a-input v-model="customer.secondary_phone" style="width: 50%" placeholder="Secondary Phone" />
                  <a-input v-model="customer.secondary_phone_extension" style="width: 50%" placeholder="Extension" />
                </a-input-group>
              </a-form-model-item>

              <!-- WeChat -->
              <a-form-model-item label="WeChat">
                <a-input v-model="customer.wechat" placeholder="WeChat" />
              </a-form-model-item>

              <!-- Wechat Added? -->
              <a-form-model-item label="WeChat Added">
                <a-radio-group v-model="customer.wechat_added" :defaultValue="false">
                  <a-radio-button :value="true"> Yes </a-radio-button>
                  <a-radio-button :value="false"> No </a-radio-button>
                </a-radio-group>
              </a-form-model-item>

              <!-- Address Search  -->
              <a-form-model-item label="Search Address">
                <PlaceAutocompleteInput
                  id="address"
                  class="ant-input"
                  placeholder="Type to search address"
                  @placechanged="onPlaceChanged"
                />
              </a-form-model-item>

              <!-- Address -->
              <a-form-model-item label="Full Address">
                <a-input v-model="customer.raw_address" />
              </a-form-model-item>

              <!-- Location -->
              <a-form-model-item label="Location">
                <a-input v-model="customer.location" />
              </a-form-model-item>

              <!-- Primary Language -->
              <a-form-model-item label="Primary Language">
                <a-input-group>
                  <a-select v-model="customer.locale" default-value="en-US" style="width: 100%">
                    <a-select-option value="en-US"> English (U.S) </a-select-option>
                    <a-select-option value="fr-FR"> French (France) </a-select-option>
                    <a-select-option value="zh-CN"> 中文 (中国大陆) </a-select-option>
                  </a-select>
                </a-input-group>
              </a-form-model-item>

              <!-- Notification Methods -->
              <a-form-model-item label="Notification Methods">
                <a-checkbox-group v-model="customer.notification_methods">
                  <a-checkbox value="email"> Email </a-checkbox>
                  <a-checkbox value="sms"> SMS </a-checkbox>
                </a-checkbox-group>
              </a-form-model-item>

              <!-- Source -->
              <a-form-model-item label="Source">
                <a-input-group>
                  <a-select v-model="customer.source" default-value="unknown" style="width: 100%">
                    <a-select-option
                      v-for="option in options_['GENERAL_SOURCES']"
                      :key="option.key"
                      :value="option.key"
                    >
                      {{ option.value }}
                    </a-select-option>
                  </a-select>
                </a-input-group>
              </a-form-model-item>

              <!-- Referer -->
              <a-form-model-item label="Referer">
                <a-input v-model="customer.referer" />
              </a-form-model-item>

              <!-- Note -->
              <a-form-model-item label="Note">
                <a-input v-model="customer.note" type="textarea" :rows="4" />
              </a-form-model-item>

              <!-- Status -->
              <a-form-model-item label="Status" prop="status">
                <a-select v-model="customer.status" default-value="active">
                  <a-select-option v-for="option in options_['CUSTOMER_STATUS']" :key="option.key" :value="option.key">
                    {{ option.value }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>

              <!-- Active -->
              <a-form-model-item label="Active" prop="active">
                <a-switch v-model="customer.active" />
              </a-form-model-item>

              <!-- Actions -->
              <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
                <a-button type="primary" @click="onSave"> Update </a-button>
                <a-button style="margin-left: 10px" @click="$router.push('/crm/customers')"> Cancel </a-button>
              </a-form-model-item>
            </a-form-model>
          </a-tab-pane>
          <a-tab-pane :key="2" tab="Projects" class="py-4">
            <div class="card p-1">
              <a-page-header title="All Projects List">
                <template slot="extra">
                  <a-button key="1" type="primary" size="small" @click="onNewProject"> New Project </a-button>
                </template>
              </a-page-header>
              <ProjectsListTable :projects="projects" />
            </div>
          </a-tab-pane>
          <a-tab-pane :key="3" tab="History" class="px-2 py-4">
            <div class="card p-3">
              <div class="input mb-2">
                <a-input-search
                  placeholder="Type what happened here..."
                  enter-button="Submit"
                  size="large"
                  @search="onSubmitHistory"
                />
              </div>
              <div class="histories">
                <a-list v-if="histories && histories.length > 0" item-layout="horizontal" :data-source="histories">
                  <a-list-item slot="renderItem" slot-scope="history, index">
                    <a-list-item-meta :description="history.content">
                      <span slot="title"> {{ history.created_at | moment('lll') }}</span>
                      <a-avatar slot="avatar" :size="45" shape="square"> {{ index + 1 }}</a-avatar>
                    </a-list-item-meta>
                  </a-list-item>
                </a-list>
                <a-empty v-else />
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import options from '@/options'
import { ProjectsListTable } from '@/components/reno/tables'
import { PlaceAutocompleteInput } from '@/components/reno/inputs'

export default {
  components: {
    ProjectsListTable,
    PlaceAutocompleteInput,
  },
  data() {
    return {
      options,
      current_tab: 1,
      customer: {
        notification_methods: ['email'],
        source: 'Unknown',
        raw_address: null,
        location: null,
        active: true,
      },
      labelCol: { xs: 24, sm: 3 },
      wrapperCol: { xs: 24, sm: 10 },
    }
  },
  watch: {
    current_tab(key) {
      if (this.customer) {
        if (key === 2) {
          store.dispatch('crm/fetchProjectsByCustomer', this.customer._id)
        }
        if (key === 3) {
          store.dispatch('crm/fetchHistory')
        }
      }
    },
  },
  computed: {
    projects: () => store.getters['crm/data']('projects') || null,
    histories: () => store.getters['crm/data']('histories') || null,
  },
  methods: {
    onNewProject() {
      this.current_tab = 2
      this.$store.dispatch('pm/setNewProjectCustomer', this.customer)
      this.$store.dispatch('pm/open', 'CREATE_PROJECT')
    },
    onPlaceChanged(place) {
      if (place) {
        // console.log(place)
        if (place.formatted_address) {
          this.customer.raw_address = place.formatted_address
        }
        if (place.locality && place.administrative_area_level_1) {
          this.customer.location = `${place.locality},${place.administrative_area_level_1}`
        }
      }
    },
    onSubmitHistory(content) {
      if (content && content !== '') {
        this.$store.dispatch('crm/insertHistory', content)
      }
    },
    async onSave() {
      if (this.customer) {
        const customer = await store.dispatch('crm/updateCustomer', this.customer)
        if (customer) {
          this.customer = customer
          this.$message.success(`Customer ${customer.name} has been updated successfully!`)
        } else {
          this.$message.error('Oops, The customer update failed!')
        }
      }
    },
  },
  async created() {
    if (this.$route.params.id) {
      this.customer = await store.dispatch('crm/fetchCustomer', this.$route.params.id)
    }
  },
}
</script>

<style lang="scss" scoped>
.ant-tabs-tabpane {
  border-top: 1px solid #dee2e6;
}
</style>
